/**
 * CoWork composer bar — the inline control row above the chat input in CoWork
 * mode (hybrid CoWork). It's the folder hub (switch / search / open, shown even
 * with no folder yet) plus HOW autonomously (Ask · Auto · Full), WHERE commands
 * run (native / WSL2), and a Context-panel toggle — compact dropdown pills with
 * tooltips, so folder + controls live with the composer, not the sidebar.
 * Desktop-only.
 *
 * @module components/cowork/cowork-composer-bar
 */

import { useState } from "react";
import { Check, ChevronDown, FolderOpen, HardDrive, PanelRight, Search, Shield, ShieldAlert, ShieldCheck, Terminal } from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useCowork } from "@/hooks/use-cowork";
import { useCoworkStore } from "@/store/cowork-store";
import { cn } from "@/lib/utils";
import type { Autonomy, ExecTier } from "@/lib/desktop";

const AUTONOMY: Array<{ value: Autonomy; label: string; hint: string }> = [
  { value: "ask", label: "Ask", hint: "Confirm every edit and command" },
  { value: "auto", label: "Auto", hint: "Auto-apply edits; confirm commands" },
  { value: "full", label: "Full", hint: "Act without asking" },
];
const TIERS: Array<{ value: ExecTier; label: string; hint: string }> = [
  { value: "native", label: "This machine", hint: "Run commands directly on your host" },
  { value: "wsl2", label: "WSL2 (Linux VM)", hint: "Run commands in the WSL2 Linux VM — a stronger boundary" },
];

// Autonomy is a safety axis, so the pill is colour-coded by risk (calm → hot),
// with a distinct icon per level so it isn't colour-only. The tier/folder pills
// stay neutral — "where commands run" isn't a risk level.
const AUTONOMY_TONE: Record<Autonomy, { Icon: typeof Shield; trigger: string }> = {
  ask: { Icon: ShieldCheck, trigger: "border-success/30 bg-success/10 text-success hover:bg-success/15" },
  auto: { Icon: Shield, trigger: "border-warning/40 bg-warning/10 text-warning hover:bg-warning/15" },
  full: { Icon: ShieldAlert, trigger: "border-destructive/40 bg-destructive/10 text-destructive hover:bg-destructive/15" },
};

const leaf = (p: string) => p.split(/[\\/]/).filter(Boolean).pop() ?? p;

export function CoworkComposerBar({ showContext = true }: { showContext?: boolean }) {
  const {
    desktop,
    mode,
    deviceId,
    folderName,
    folderPath,
    autonomy,
    setAutonomy,
    execTier,
    setExecTier,
    capabilities,
    recentFolders,
    openFolder,
    openRecent,
  } = useCowork();
  const contextCollapsed = useCoworkStore((s) => s.contextPanelCollapsed);
  const toggleContext = useCoworkStore((s) => s.toggleContextPanel);
  const [query, setQuery] = useState("");
  if (!desktop || mode !== "cowork") return null;

  const autonomyLabel = AUTONOMY.find((a) => a.value === autonomy)?.label ?? "Ask";
  const tierLabel = TIERS.find((t) => t.value === execTier)?.label ?? "This machine";
  const wslAvailable = capabilities?.wsl2.available === true;
  const q = query.trim().toLowerCase();
  const recents = recentFolders
    .filter((p) => p !== folderPath)
    .filter((p) => !q || p.toLowerCase().includes(q));

  return (
    <div className="mb-1.5 flex flex-wrap items-center gap-1.5 px-1">
      {/* Folder switcher — always present (opens the first folder too) */}
      <DropdownMenu>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger
              className={cn(
                "inline-flex min-w-0 max-w-[45%] items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring",
                deviceId
                  ? "border-secondary/30 bg-secondary/10 text-secondary hover:bg-secondary/15"
                  : "border-dashed border-border bg-muted/60 text-muted-foreground hover:text-foreground",
              )}
            >
              <HardDrive className="size-3.5 shrink-0" />
              <span className="truncate">{deviceId ? folderName : "Open a folder"}</span>
              <ChevronDown className="size-3 shrink-0 opacity-70" />
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side="top">{deviceId ? "Switch working folder" : "Open a folder"}</TooltipContent>
        </Tooltip>
        <DropdownMenuContent align="start" side="top" sideOffset={6} className="w-72 p-1">
          {recentFolders.length > 3 && (
            <div className="mb-1 flex items-center gap-2 border-b border-border/60 px-2 pb-1.5">
              <Search className="size-3.5 shrink-0 text-muted-foreground" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                placeholder="Search folders…"
                className="w-full bg-transparent py-1 text-sm outline-none placeholder:text-muted-foreground"
              />
            </div>
          )}

          {deviceId && folderPath && !q && (
            <>
              <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-muted-foreground">
                Working folder
              </DropdownMenuLabel>
              <DropdownMenuItem className="gap-2" onSelect={(e) => e.preventDefault()}>
                <HardDrive className="size-4 shrink-0 text-secondary" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">{folderName}</div>
                  <div className="truncate text-[11px] text-muted-foreground">{folderPath}</div>
                </div>
                <Check className="size-4 shrink-0 text-secondary" />
              </DropdownMenuItem>
            </>
          )}

          {recents.length > 0 && (
            <>
              <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-muted-foreground">
                Recent
              </DropdownMenuLabel>
              <div className="max-h-[240px] overflow-y-auto">
                {recents.map((p) => (
                  <DropdownMenuItem key={p} className="gap-2" onClick={() => openRecent(p)}>
                    <FolderOpen className="size-4 shrink-0 text-muted-foreground" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm">{leaf(p)}</div>
                      <div className="truncate text-[11px] text-muted-foreground">{p}</div>
                    </div>
                  </DropdownMenuItem>
                ))}
              </div>
            </>
          )}

          {q && recents.length === 0 && (
            <div className="px-2 py-2 text-xs text-muted-foreground">No matching folders.</div>
          )}

          <DropdownMenuSeparator />
          <DropdownMenuItem className="gap-2 font-medium" onClick={openFolder}>
            <FolderOpen className="size-4 shrink-0" />
            Choose a folder…
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Agent controls — only meaningful once a folder is connected */}
      {deviceId && (
        <>
          <Pill
            icon={AUTONOMY_TONE[autonomy].Icon}
            label={autonomyLabel}
            tooltip="Agent autonomy"
            tone={AUTONOMY_TONE[autonomy].trigger}
          >
            {AUTONOMY.map((o) => (
              <Option key={o.value} active={autonomy === o.value} label={o.label} hint={o.hint} onClick={() => setAutonomy(o.value)} />
            ))}
          </Pill>

          {wslAvailable && (
            <Pill icon={Terminal} label={tierLabel} tooltip="Where commands run">
              {TIERS.map((o) => (
                <Option key={o.value} active={execTier === o.value} label={o.label} hint={o.hint} onClick={() => setExecTier(o.value)} />
              ))}
            </Pill>
          )}

          {showContext && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={toggleContext}
                aria-pressed={!contextCollapsed}
                className={cn(
                  "ml-auto inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring",
                  contextCollapsed
                    ? "border-border bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground"
                    : "border-secondary/30 bg-secondary/10 text-secondary",
                )}
              >
                <PanelRight className="size-3.5" />
                Context
              </button>
            </TooltipTrigger>
            <TooltipContent side="top">{contextCollapsed ? "Show Context panel" : "Hide Context panel"}</TooltipContent>
          </Tooltip>
          )}
        </>
      )}
    </div>
  );
}

function Pill({
  icon: Icon,
  label,
  tooltip,
  tone,
  children,
}: {
  icon: typeof Shield;
  label: string;
  tooltip: string;
  /** Optional risk tint (autonomy). Absent = the neutral pill. */
  tone?: string;
  children: React.ReactNode;
}) {
  return (
    <DropdownMenu>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring",
              tone ?? "border-border bg-muted/60 text-foreground hover:bg-muted",
            )}
          >
            <Icon className={cn("size-3.5", !tone && "text-muted-foreground")} />
            {label}
            <ChevronDown className={cn("size-3", tone ? "opacity-70" : "text-muted-foreground")} />
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent side="top">{tooltip}</TooltipContent>
      </Tooltip>
      <DropdownMenuContent align="start" side="top" sideOffset={6} className="w-56 p-1">
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Option({
  active,
  label,
  hint,
  onClick,
}: {
  active: boolean;
  label: string;
  hint: string;
  onClick: () => void;
}) {
  return (
    <DropdownMenuItem
      onClick={onClick}
      className={cn(
        "flex flex-col items-start gap-0.5 rounded-md py-1.5 transition-colors",
        active && "bg-secondary/10 focus:bg-secondary/15 data-[highlighted]:bg-secondary/15",
      )}
    >
      <span className={cn("flex items-center gap-1.5 text-sm font-medium", active && "text-secondary")}>
        {label}
        {active && <Check className="size-3.5" />}
      </span>
      <span className="text-[11px] leading-tight text-muted-foreground">{hint}</span>
    </DropdownMenuItem>
  );
}
